import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { flow, throttle } from 'lodash-es';

interface UseHistogramBarBetweenHandleProps {
  leftHandlePosition: number;
  rightHandlePosition: number;
}

/** 히스토그램 바가 핸들 사이에 있는지 확인하는 훅 */

const useHistogramBarBetweenHandle = ({
  leftHandlePosition,
  rightHandlePosition,
}: UseHistogramBarBetweenHandleProps) => {
  const histogramBarRefs = useRef<HTMLDivElement[]>([]);
  const leftHandleRef = useRef<HTMLDivElement>(null);
  const rightHandleRef = useRef<HTMLDivElement>(null);

  const [barCenters, setBarCenters] = useState<number[]>([]);
  const [centerOfHandle, setCenterOfHandle] = useState<{ left: number; right: number }>({ left: 0, right: 0 });

  /** 핸들 사이에 히스토그램 바가 있는지 확인하는 함수 */
  const isHistogramBarBetweenHandle = useCallback(
    (index: number) => {
      const centerOfHistogramBar = barCenters[index];
      const { left, right } = centerOfHandle;
      return centerOfHistogramBar > left && centerOfHistogramBar < right;
    },
    [centerOfHandle, barCenters],
  );

  /** 핸들의 중심 위치를 계산하는 함수 */
  const calculateHandleCenters = useMemo(
    () =>
      throttle(() => {
        if (!leftHandleRef.current || !rightHandleRef.current) return;
        const _left = leftHandleRef.current.offsetLeft;
        const _right = rightHandleRef.current.offsetLeft;
        const [leftRect, rightRect] = _left < _right ? [_left, _right] : [_right, _left];

        const centerOfLeftHandle = leftRect + leftHandleRef.current.offsetWidth / 2;
        const centerOfRightHandle = rightRect + leftHandleRef.current.offsetWidth / 2;

        setCenterOfHandle({ left: centerOfLeftHandle, right: centerOfRightHandle });
      }, 100),
    [],
  );

  useEffect(() => {
    // 핸들의 위치가 변경될 때 핸들 중심 위치 재계산
    calculateHandleCenters();
  }, [leftHandlePosition, rightHandlePosition]);

  /** 히스토그램 바의 중심 위치를 업데이트하는 함수 */
  const updateBarCenters = useCallback(
    throttle(() => {
      if (!histogramBarRefs.current.length) return;

      const centerX = histogramBarRefs.current.map((barRef) => {
        const centerOfHistogramBar = barRef.offsetLeft + barRef.offsetWidth / 2;
        return centerOfHistogramBar;
      });
      setBarCenters(centerX);
    }, 100),
    [histogramBarRefs],
  );

  useEffect(() => {
    updateBarCenters();
    // 윈도우 크기가 변경될 때 히스토그램 바의 중심 위치와 핸들 중심 위치를 업데이트
    window.addEventListener('resize', flow(updateBarCenters, calculateHandleCenters));
    return () => {
      window.removeEventListener('resize', flow(updateBarCenters, calculateHandleCenters));
    };
  }, []);

  return {
    histogramBarRefs,
    leftHandleRef,
    rightHandleRef,

    isHistogramBarBetweenHandle,
  };
};

export default useHistogramBarBetweenHandle;
